// Engram: persistent memory for the user's agents (connect, use in chats, search). Moved here from the Models page so everything Lyra
// connects to lives on the Connections page. State and behaviour are unchanged.
import { useState, useEffect, useRef } from "react";
import {
  Brain, ToggleLeft, ToggleRight, AlertTriangle, Trash2, Download, Ban, AlertCircle, Eye,
  CheckCircle2, RotateCcw, Plus, ChevronDown, TestTube2, Loader2, Check, ExternalLink, KeyRound,
  EyeOff, LogOut, Plug, Info, X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAI } from "../../context/AIContext";

const blue    = "var(--lyra-color-primary)";
const blueBg  = "var(--app-primary-soft)";
const bord    = "var(--lyra-color-border)";
const tx      = "var(--lyra-color-text)";
const mu      = "var(--app-text-3)";
const su      = "var(--lyra-color-text-muted)";
const shadow  = "0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)";

const CSS = `
.ai-console-engram-row { display:flex; align-items:center; gap:14px; }
.ai-console-engram-title-row { display:flex; align-items:center; gap:6px; }
.ai-console-engram-actions { flex-shrink:0; display:flex; flex-direction:column; gap:5px; align-items:stretch; }
.ai-console-engram-key-row { display:flex; gap:8px; }
.ai-console-memory-search-row { display:flex; gap:8px; }
@media (max-width: 768px) {
  .ai-console-engram-row { flex-direction:column; align-items:flex-start; }
  .ai-console-engram-title-row { flex-wrap:wrap; }
  .ai-console-engram-actions { width:100%; }
  .ai-console-engram-key-row { flex-direction:column; }
  .ai-console-memory-search-row { flex-direction:column; }
}
`;

function Btn({ children, onClick, danger, primary, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        border: primary ? "none" : "1px solid " + (danger ? "var(--app-danger-border)" : bord), background: primary ? blue : danger ? "var(--lyra-color-danger-bg)" : "var(--lyra-color-surface)", color: primary ? "var(--app-on-accent)" : danger ? "var(--lyra-color-danger)" : tx, opacity: disabled ? 0.6 : 1 }}>
      {children}
    </button>
  );
}

// ─── Engram API helpers ───────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL ?? "";
function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}
const engramAPI = {
  status:     async () => { const r = await fetch(`${API_BASE}/api/engram/status`, { headers: authHeaders() }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  enable:     async () => { const r = await fetch(`${API_BASE}/api/engram/enable`, { method: "POST", headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  connectKey: async (k, url) => { const r = await fetch(`${API_BASE}/api/engram/connect-key`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ apiKey: k, ...(url ? { engramUrl: url } : {}) }) }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  disable:    async () => { const r = await fetch(`${API_BASE}/api/engram/disable`, { method: "DELETE", headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  magicLink:  async () => { const r = await fetch(`${API_BASE}/api/engram/magic-link`, { headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  toggleChat: async (en) => { const r = await fetch(`${API_BASE}/api/engram/chat`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ enabled: en }) }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
};

// ─── Engram Key Modal ─────────────────────────────────────────────────────────
function EngramKeyModal({ onClose, onConnected, anchorRef }) {
  const [phase,    setPhase]    = useState("loading");
  const [apiKey,   setApiKey]   = useState("");
  const [engramUrl,setEngramUrl]= useState("");
  const [showKey,  setShowKey]  =   useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pos,      setPos]      = useState({ top: -9999, left: -9999 });
  const [caretX,   setCaretX]   = useState(0);
  const popupRef = useRef(null);

  useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const popW = 360;
      const left = Math.min(window.innerWidth - popW - 8, Math.max(8, rect.right - popW));
      const caret = Math.max(14, Math.min(popW - 14, rect.left + rect.width / 2 - left));
      setPos({ top: rect.bottom + 10, left });
      setCaretX(caret);
    }
    function handleOutside(e) {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) { onClose(); }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function autoConnect() {
    setPhase("loading");
    try {
      const r1 = await fetch(`${API_BASE}/api/engram/enable`, { method: "POST", headers: authHeaders() });
      const d1 = await r1.json();
      if (d1.success || d1.alreadyConnected) {
        const r2 = await fetch(`${API_BASE}/api/agents/engram-key`, { headers: authHeaders() });
        const d2 = await r2.json();
        if (d2.engramApiKey) { setApiKey(d2.engramApiKey); setPhase("loaded"); }
        else                 { setPhase("nokey"); }
      } else if (d1.manualKeyRequired) {
        setPhase("nokey");
      } else {
        setErrorMsg(d1.message || "Connection failed"); setPhase("error");
      }
    } catch (err) {
      setErrorMsg(err.message || "Network error"); setPhase("error");
    }
  }

  useEffect(() => { autoConnect(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUseKey() {
    if (!apiKey) return;
    setPhase("connecting");
    try {
      const body = { apiKey };
      if (engramUrl.trim()) body.engramUrl = engramUrl.trim();
      const r = await fetch(`${API_BASE}/api/engram/connect-key`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.success) { onConnected(apiKey); onClose(); }
      else           { setErrorMsg(d.message || "Failed to connect"); setPhase("error"); }
    } catch (err) {
      setErrorMsg(err.message || "Network error"); setPhase("error");
    }
  }

  const masked = showKey ? apiKey : apiKey ? apiKey.slice(0, 8) + "••••••••••••••••••••" + apiKey.slice(-4) : "";
  const phaseLabel = { loading: "Authenticating via Lyra…", connecting: "Saving your key…", loaded: "API key ready", nokey: "Manual setup required", error: "Something went wrong" };

  if (pos.top < 0) return null;

  return (
    <>
      <style>{`
        @keyframes egFadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes egPopIn  { from { opacity:0; transform:translateY(-8px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes egDot1   { 0%,60%,100% { transform:scale(0.5); opacity:0.3 } 20% { transform:scale(1.15); opacity:1 } }
        @keyframes egDot2   { 0%,20%,80%,100% { transform:scale(0.5); opacity:0.3 } 40% { transform:scale(1.15); opacity:1 } }
        @keyframes egDot3   { 0%,40%,100% { transform:scale(0.5); opacity:0.3 } 60% { transform:scale(1.15); opacity:1 } }
        @keyframes egSpin   { to { transform:rotate(360deg) } }
        @keyframes egPulse  { 0%,100% { box-shadow:0 0 0 0 rgba(124,58,237,0.5) } 50% { box-shadow:0 0 0 10px rgba(124,58,237,0) } }
      `}</style>

      <div ref={popupRef} style={{ position: "fixed", top: pos.top, left: pos.left, width: 360, zIndex: 9999, animation: "egPopIn 0.22s cubic-bezier(0.34,1.4,0.64,1)" }}>
        {/* Caret pointing up toward the button */}
        <div style={{ position: "absolute", top: -7, left: caretX - 7, width: 14, height: 14, background: "var(--lyra-color-primary)", transform: "rotate(45deg)", borderRadius: "3px 0 0 0", zIndex: 1 }} />

        {/* Card */}
        <div style={{ position: "relative", zIndex: 2, background: "var(--lyra-color-surface)", borderRadius: 14, boxShadow: "0 8px 36px rgba(80,20,200,0.2), 0 2px 10px rgba(0,0,0,0.09)", overflow: "hidden" }}>

          {/* Purple header */}
          <div style={{ background: "linear-gradient(135deg, var(--lyra-color-primary) 0%, var(--app-primary-deep) 55%, #4c1d95 100%)", padding: "13px 14px", display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
            <button onClick={onClose} style={{ position: "absolute", top: 9, right: 9, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 7, width: 26, height: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--app-on-accent)" }}>
              <X size={13} />
            </button>
            <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: "rgba(255,255,255,0.14)", border: "1.5px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", animation: phase === "loading" ? "egPulse 1.6s ease-out infinite" : "none" }}>
              <Brain size={17} color="var(--app-on-accent)" />
            </div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--app-on-accent)" }}>Engram Memory</div>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.65)", marginTop: 1 }}>{phaseLabel[phase]}</div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "14px 14px 12px" }}>

            {/* LOADING */}
            {phase === "loading" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "8px 0 4px", animation: "egFadeIn 0.3s ease" }}>
                <div style={{ display: "flex", gap: 8, justifyContent: 'center' }}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={{ width: 11, height: 11, borderRadius: "50%", background: i === 1 ? "var(--lyra-color-primary)" : i === 2 ? "var(--lyra-color-primary)" : "#c4b5fd", animation: `egDot${i} 1.4s ease-in-out infinite` }} />
                  ))}
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--lyra-color-text)", marginBottom: 2 }}>Connecting to Engram</div>
                  <div style={{ fontSize: 11.5, color: "var(--app-text-3)" }}>No login needed · using your Lyra session</div>
                </div>
              </div>
            )}

            {/* LOADED */}
            {phase === "loaded" && (
              <div style={{ animation: "egFadeIn 0.3s ease" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <CheckCircle2 size={13} color="var(--lyra-color-success)" />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--lyra-color-success)" }}>Key retrieved from your account</span>
                </div>
                <div style={{ background: "var(--app-primary-soft)", border: "1.5px solid var(--app-primary-soft-2)", borderRadius: 9, padding: "8px 10px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{ flex: 1, fontSize: 11.5, color: "#3b0764", fontFamily: "'Fira Code','Courier New',monospace", wordBreak: "break-all" }}>{masked}</code>
                  <button onClick={() => setShowKey(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--lyra-color-primary)", padding: 2, flexShrink: 0, display: "flex" }}>
                    {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
                <button onClick={handleUseKey} style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, var(--lyra-color-primary) 0%, var(--app-primary-deep) 100%)", color: "var(--app-on-accent)", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, boxShadow: "0 3px 12px rgba(124,58,237,0.3)" }}>
                  <Check size={14} strokeWidth={2.5} /> Use this key &amp; connect
                </button>
              </div>
            )}

            {/* CONNECTING */}
            {phase === "connecting" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "12px 0 6px", animation: "egFadeIn 0.3s ease" }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", border: "3px solid var(--app-primary-soft)", borderTop: "3px solid var(--lyra-color-primary)", animation: "egSpin 0.75s linear infinite" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--lyra-color-text)", marginBottom: 2 }}>Connecting…</div>
                  <div style={{ fontSize: 11.5, color: "var(--app-text-3)" }}>Verifying and saving your key</div>
                </div>
              </div>
            )}

            {/* NO KEY — manual entry */}
            {phase === "nokey" && (
              <div style={{ animation: "egFadeIn 0.3s ease" }}>
                <div style={{ background: "var(--lyra-color-warning-bg)", border: "1px solid var(--app-warning-border)", borderRadius: 9, padding: "9px 11px", marginBottom: 12, display: "flex", gap: 8 }}>
                  <AlertTriangle size={13} color="var(--lyra-color-warning)" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--lyra-color-warning)", marginBottom: 2 }}>Auto-provisioning unavailable</div>
                    <div style={{ fontSize: 11.5, color: "#a16207" }}>Paste your Engram API key below (starts with <code style={{ fontFamily: "monospace" }}>egm_</code>).</div>
                  </div>
                </div>
                {/* Manual key input */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--lyra-color-warning)", marginBottom: 4 }}>API key</div>
                  <div style={{ display: "flex", gap: 7 }}>
                    <input
                      type="text"
                      placeholder="egm_••••••••••••••••"
                      value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      style={{ flex: 1, padding: "7px 10px", border: "1.5px solid var(--app-primary-soft-2)", borderRadius: 8, fontSize: 12, color: "#3b0764", fontFamily: "'Fira Code','Courier New',monospace", background: "var(--lyra-color-surface-2)", outline: "none" }}
                      onFocus={e => { e.target.style.borderColor = "var(--lyra-color-primary)"; }}
                      onBlur={e => { e.target.style.borderColor = "var(--app-primary-soft-2)"; }}
                    />
                    <button
                      onClick={handleUseKey}
                      disabled={!apiKey.trim().startsWith("egm_")}
                      style={{ padding: "7px 13px", borderRadius: 8, border: "none", background: apiKey.trim().startsWith("egm_") ? "var(--lyra-color-primary)" : "var(--lyra-color-border)", color: apiKey.trim().startsWith("egm_") ? "var(--lyra-color-surface)" : "var(--app-text-3)", fontWeight: 600, fontSize: 12, cursor: apiKey.trim().startsWith("egm_") ? "pointer" : "default", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
                    >
                      <Plug size={12} /> Connect
                    </button>
                  </div>
                </div>
                <a href="https://engram-tcac.onrender.com/dashboard" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11.5, color: "var(--lyra-color-primary)", textDecoration: "none" }}>
                  <ExternalLink size={11} /> Get your key from the Engram dashboard
                </a>
              </div>
            )}

            {/* ERROR */}
            {phase === "error" && (
              <div style={{ animation: "egFadeIn 0.3s ease" }}>
                <div style={{ background: "var(--lyra-color-danger-bg)", border: "1px solid var(--app-danger-border)", borderRadius: 9, padding: "9px 11px", marginBottom: 10, display: "flex", gap: 8 }}>
                  <AlertCircle size={13} color="var(--lyra-color-danger)" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 11.5, color: "var(--lyra-color-danger)" }}>{errorMsg}</div>
                </div>
                <button onClick={autoConnect} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 11px", borderRadius: 8, border: "1.5px solid var(--app-primary-soft-2)", background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <RotateCcw size={12} /> Retry
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

export default function EngramMemory() {
  const { engramChatEnabled, setEngramChatEnabled } = useAI();
  // ── Engram state ──
  const [engramConnected,    setEngramConnected]    = useState(false);
  const [engramLoading,      setEngramLoading]      = useState(true);
  const [removing,           setRemoving]           = useState(false);
  const [togglingChat,       setTogglingChat]       = useState(false);
  const [openingDashboard,   setOpeningDashboard]   = useState(false);
  const [showEngramModal,    setShowEngramModal]    = useState(false);
  const [storedKey,          setStoredKey]          = useState("");
  const [editKey,            setEditKey]            = useState("");
  const [keyDirty,           setKeyDirty]           = useState(false);
  const [showEditKey,        setShowEditKey]        = useState(false);
  const [storedUrl,          setStoredUrl]          = useState("");
  const [editUrl,            setEditUrl]            = useState("");
  const [urlDirty,           setUrlDirty]           = useState(false);
  const [savingRekey,        setSavingRekey]        = useState(false);
  const enableBtnRef = useRef(null);
  // Memory search panel
  const [memScope,           setMemScope]           = useState("all");
  const [memIsLatest,        setMemIsLatest]        = useState(true);
  const [memQuery,           setMemQuery]           = useState("");
  const [memResults,         setMemResults]         = useState(null);
  const [memSearching,       setMemSearching]       = useState(false);

  useEffect(() => {
    engramAPI.status()
      .then(d => { setEngramConnected(d.connected); if (d.engramChatEnabled !== undefined) setEngramChatEnabled(d.engramChatEnabled); })
      .catch(() => setEngramConnected(false))
      .finally(() => setEngramLoading(false));
  }, []);

  async function handleEngramRemove() {
    setRemoving(true);
    try { await engramAPI.disable(); setEngramConnected(false); setEngramChatEnabled(false); toast.success("Engram memory disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setRemoving(false); }
  }

  // Sync editable key field with server state
  useEffect(() => {
    if (!engramConnected) { setEditKey(""); setStoredKey(""); setKeyDirty(false); setEditUrl(""); setStoredUrl(""); setUrlDirty(false); return; }
    fetch(`${API_BASE}/api/agents/engram-key`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.engramApiKey) { setStoredKey(d.engramApiKey); setEditKey(d.engramApiKey); setKeyDirty(false); }
        if (d.engramBaseUrl) { setStoredUrl(d.engramBaseUrl); setEditUrl(d.engramBaseUrl); setUrlDirty(false); }
      })
      .catch(() => {});
  }, [engramConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleReconnectKey() {
    setSavingRekey(true);
    try {
      await engramAPI.connectKey(editKey.trim(), editUrl.trim() || undefined);
      setStoredKey(editKey.trim());
      if (editUrl.trim()) { setStoredUrl(editUrl.trim()); setUrlDirty(false); }
      setKeyDirty(false);
      toast.success("Engram settings updated!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingRekey(false);
    }
  }

  async function handleOpenDashboard() {
    setOpeningDashboard(true);
    try { const { url } = await engramAPI.magicLink(); window.open(url, "_blank", "noopener,noreferrer"); }
    catch { toast.error("Could not open Engram dashboard."); }
    finally { setOpeningDashboard(false); }
  }

  async function handleToggleChat() {
    setTogglingChat(true);
    try { const { engramChatEnabled: updated } = await engramAPI.toggleChat(!engramChatEnabled); setEngramChatEnabled(updated); }
    catch (err) { toast.error(err.message); }
    finally { setTogglingChat(false); }
  }

  async function handleMemSearch() {
    if (!memQuery.trim()) return;
    setMemSearching(true);
    try {
      const scopeMap = { all: [], user: [], agent: [], chain: [], documents: [] };
      const typeMap  = { documents: "document", all: "all", user: "all", agent: "all", chain: "all" };
      const r = await fetch(`${typeof API_BASE !== "undefined" ? API_BASE : ""}/api/engram/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          query:      memQuery.trim(),
          type:       typeMap[memScope] ?? "all",
          scopeTags:  scopeMap[memScope] ?? [],
          onlyLatest: memIsLatest,
          limit:      20,
        }),
      });
      const d = await r.json();
      setMemResults(d.results ?? []);
    } catch (err) {
      toast.error("Memory search failed: " + err.message);
      setMemResults([]);
    } finally {
      setMemSearching(false);
    }
  }

  return (
    <>
      <style>{CSS}</style>
          <div style={{ minWidth: 0 }}>

            {/* Engram connection row */}
            <div style={{ padding: "14px 0", borderBottom: "1px solid " + bord }}>
              <div className="ai-console-engram-row">
                <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 10, background: engramConnected ? "var(--app-primary-soft)" : "color-mix(in srgb, var(--lyra-color-primary) 8%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>
                  <Brain size={19} color={engramConnected ? "var(--lyra-color-primary)" : "var(--lyra-color-primary)"} />
                </div>

                <div style={{ flex: 2, minWidth: 0 }}>
                  <div className="ai-console-engram-title-row" style={{ flexWrap: "nowrap" }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: tx }}>Engram Memory</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)" }}>QDRANT</span>
                  </div>
                  <div style={{ fontSize: 12, color: mu, marginTop: 2 }}>
                    {engramLoading ? "Checking..." : engramConnected
                      ? "Personal memory space · agents inject your past context automatically"
                      : "Persistent vector memory for all your agents"}
                  </div>
                </div>

                <div className="ai-console-engram-actions">
                  {engramLoading ? (
                    <Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} />
                  ) : engramConnected ? (
                    <>
                      <button onClick={handleOpenDashboard} disabled={openingDashboard} style={{ padding: "6px 11px", borderRadius: 8, border: "1.5px solid var(--app-primary-soft-2)", cursor: "pointer", fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 5, background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)", opacity: openingDashboard ? 0.6 : 1, whiteSpace: "nowrap" }}>
                        {openingDashboard ? <Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} /> : <ExternalLink size={12} />}
                        Manage memories
                      </button>
                      <button onClick={handleEngramRemove} disabled={removing} style={{ padding: "6px 11px", borderRadius: 8, border: "1.5px solid var(--app-danger-border)", cursor: "pointer", fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 5, background: "var(--lyra-color-danger-bg)", color: "var(--lyra-color-danger)", whiteSpace: "nowrap" }}>
                        {removing ? <Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} /> : <LogOut size={13} />}
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button ref={enableBtnRef} onClick={() => setShowEngramModal(true)} style={{ padding: "7px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6, background: "var(--lyra-color-primary)", color: "var(--app-on-accent)", transition: "background 0.15s" }}>
                      <Plug size={13} />Enable Memory
                    </button>
                  )}
                </div>
              </div>

              {/* Engram URL + Key fields — shown when connected */}
              {engramConnected && editKey && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {/* URL row */}
                  <input
                    type="text"
                    placeholder="Engram server URL (e.g. http://localhost:4000)"
                    value={editUrl}
                    onChange={e => { setEditUrl(e.target.value); setUrlDirty(e.target.value !== storedUrl); }}
                    style={{ width: "100%", padding: "7px 10px", border: `1.5px solid ${urlDirty ? "var(--lyra-color-primary)" : "var(--app-primary-soft-2)"}`, borderRadius: 8, fontSize: 11.5, color: "#3b0764", fontFamily: "'Fira Code','Courier New',monospace", background: "var(--lyra-color-surface-2)", boxSizing: "border-box", outline: "none", transition: "border-color 0.15s" }}
                    onFocus={e => { e.target.style.borderColor = "var(--lyra-color-primary)"; }}
                    onBlur={e => { if (!urlDirty) e.target.style.borderColor = "var(--app-primary-soft-2)"; }}
                  />
                  {/* Key + action button row */}
                  <div className="ai-console-engram-key-row">
                    <div style={{ flex: 1, position: "relative" }}>
                      <input
                        type={showEditKey ? "text" : "password"}
                        value={editKey}
                        onChange={e => { setEditKey(e.target.value); setKeyDirty(e.target.value !== storedKey); }}
                        style={{ width: "100%", padding: "7px 30px 7px 10px", border: `1.5px solid ${keyDirty ? "var(--lyra-color-primary)" : "var(--app-primary-soft-2)"}`, borderRadius: 8, fontSize: 12, color: "#3b0764", fontFamily: "'Fira Code','Courier New',monospace", background: "var(--lyra-color-surface-2)", boxSizing: "border-box", outline: "none", transition: "border-color 0.15s" }}
                        onFocus={e => { e.target.style.borderColor = "var(--lyra-color-primary)"; }}
                        onBlur={e => { if (!keyDirty) e.target.style.borderColor = "var(--app-primary-soft-2)"; }}
                      />
                      <button type="button" onClick={() => setShowEditKey(v => !v)} style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--lyra-color-primary)", padding: 2, display: "flex" }}>
                        {showEditKey ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </div>
                    <button
                      onClick={(keyDirty || urlDirty) ? handleReconnectKey : undefined}
                      disabled={savingRekey || ((keyDirty || urlDirty) && !editKey.trim())}
                      style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: (keyDirty || urlDirty) ? "var(--lyra-color-primary)" : "var(--lyra-color-success-bg)", color: (keyDirty || urlDirty) ? "var(--lyra-color-surface)" : "var(--lyra-color-success)", fontWeight: 600, fontSize: 12, flexShrink: 0, cursor: (keyDirty || urlDirty) && editKey.trim() && !savingRekey ? "pointer" : "default", display: "flex", alignItems: "center", gap: 5, transition: "background 0.15s, color 0.15s" }}
                    >
                      {savingRekey
                        ? <><Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} />Saving…</>
                        : (keyDirty || urlDirty) ? <><Plug size={12} />Save</> : <><Check size={12} />Connected</>}
                    </button>
                  </div>
                  {!storedUrl && !editUrl && (
                    <div style={{ fontSize: 11, color: "var(--lyra-color-warning)", display: "flex", alignItems: "center", gap: 4 }}>
                      <AlertTriangle size={11} /> No Engram URL set — enter your server URL above for memory to work
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Use Engram in chats toggle */}
            <div style={{ marginTop: 14, background: engramChatEnabled ? "var(--app-primary-soft)" : "var(--lyra-color-surface-2)", border: `1px solid ${engramChatEnabled ? "var(--app-primary-soft-2)" : bord}`, borderRadius: 12, padding: "14px 16px", transition: "all 0.2s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Brain size={20} color={engramConnected && engramChatEnabled ? "var(--lyra-color-primary)" : mu} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: engramChatEnabled ? "var(--lyra-color-primary)" : tx }}>Use Engram memory in chats</div>
                  <div style={{ fontSize: 12, color: mu, marginTop: 2 }}>
                    {!engramConnected
                      ? "Connect Engram above to enable chat memory"
                      : engramChatEnabled
                        ? "✓ Active — chat messages retrieve & save to your personal memory space"
                        : "Off — only agent runs use Engram. Toggle on to include chat sessions."}
                  </div>
                </div>
                <button
                  onClick={handleToggleChat}
                  disabled={togglingChat || !engramConnected}
                  title={!engramConnected ? "Connect Engram first" : ""}
                  style={{ position: "relative", width: 42, height: 22, borderRadius: 999, border: "none", padding: 0, outline: "none", flexShrink: 0, cursor: !engramConnected || togglingChat ? "not-allowed" : "pointer", background: !engramConnected ? "var(--lyra-color-border)" : engramChatEnabled ? "var(--lyra-color-primary)" : "var(--app-border-strong)", transition: "background 0.2s", opacity: togglingChat ? 0.6 : 1 }}
                >
                  <span style={{ position: "absolute", top: 4, left: engramChatEnabled && engramConnected ? 22 : 4, width: 14, height: 14, borderRadius: "50%", background: "var(--lyra-color-surface)", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.2s", display: "block" }} />
                </button>
              </div>
              {engramChatEnabled && engramConnected && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--lyra-color-primary)", display: "flex", alignItems: "center", gap: 5 }}>
                  <CheckCircle2 size={12} color="var(--lyra-color-primary)" />
                  Engram active for chats · memories scoped to your personal key · agents always use memory
                </div>
              )}
            </div>

            {/* Memory search panel — only when connected */}
            {engramConnected && (
              <div style={{ marginTop: 18, padding: "14px 16px", background: "var(--app-primary-soft)", borderRadius: 12, border: "1px solid var(--app-primary-soft-2)" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--app-primary-deep)", marginBottom: 10 }}>Search Memories</div>
                {/* Scope filter pills */}
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                  {["all", "user", "agent", "chain", "documents"].map(s => (
                    <button key={s} onClick={() => setMemScope(s)}
                      style={{ padding: "3px 10px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, border: `1.5px solid ${memScope === s ? "var(--lyra-color-primary)" : "var(--app-primary-soft-2)"}`, background: memScope === s ? "var(--lyra-color-primary)" : "var(--lyra-color-surface)", color: memScope === s ? "var(--lyra-color-surface)" : "var(--app-primary-deep)", cursor: "pointer", textTransform: "capitalize" }}>
                      {s}
                    </button>
                  ))}
                  <label style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto", fontSize: 11.5, color: "var(--app-primary-deep)", cursor: "pointer" }}>
                    <input type="checkbox" checked={memIsLatest} onChange={e => setMemIsLatest(e.target.checked)} style={{ accentColor: "var(--lyra-color-primary)" }} />
                    Only latest
                  </label>
                </div>
                {/* Search input + button */}
                <div className="ai-console-memory-search-row">
                  <input
                    value={memQuery}
                    onChange={e => setMemQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleMemSearch(); }}
                    placeholder="Search your memories…"
                    style={{ flex: 1, padding: "7px 12px", borderRadius: 8, border: "1.5px solid var(--app-primary-soft-2)", fontSize: 12.5, outline: "none", fontFamily: "inherit", background: "var(--lyra-color-surface)" }}
                  />
                  <button onClick={handleMemSearch} disabled={memSearching || !memQuery.trim()}
                    style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: memQuery.trim() ? "var(--lyra-color-primary)" : "var(--lyra-color-border)", color: memQuery.trim() ? "var(--lyra-color-surface)" : mu, fontWeight: 600, fontSize: 12.5, cursor: memQuery.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 5 }}>
                    {memSearching ? <Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} /> : <Brain size={12} />} Search
                  </button>
                </div>
                {/* Results */}
                {memResults !== null && (
                  <div style={{ marginTop: 10 }}>
                    {memResults.length === 0 ? (
                      <div style={{ fontSize: 12, color: mu, textAlign: "center", padding: "8px 0" }}>No memories found.</div>
                    ) : (
                      memResults.map((m, i) => (
                        <div key={i} style={{ padding: "8px 0", borderBottom: i < memResults.length - 1 ? "1px solid var(--app-primary-soft)" : "none" }}>
                          <div style={{ fontSize: 12.5, color: "var(--lyra-color-text)", fontWeight: 500, lineHeight: 1.4 }}>{m.content}</div>
                          <div style={{ fontSize: 11, color: mu, marginTop: 2, display: "flex", gap: 8 }}>
                            <span style={{ background: "var(--app-primary-soft)", color: "var(--app-primary-deep)", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>{m.type}</span>
                            {m.score !== undefined && <span>relevance: {(m.score * 100).toFixed(0)}%</span>}
                            {m.metadata?.source && <span>{m.metadata.source}</span>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Pinned memories */}
          </div>
      {/* Engram key popup */}
      {showEngramModal && (
        <EngramKeyModal
          anchorRef={enableBtnRef}
          onClose={() => setShowEngramModal(false)}
          onConnected={(key) => {
            setEngramConnected(true);
            setStoredKey(key);
            setEditKey(key);
            setKeyDirty(false);
            setShowEngramModal(false);
            toast.success("Engram memory connected! Your agents now have persistent context.");
          }}
        />
      )}
    </>
  );
}
